import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthenticationGuard from '../../components/ui/AuthenticationGuard';
import LoginForm from './components/LoginForm';
import OAuthButtons from './components/OAuthButtons';
import SecurityBadges from './components/SecurityBadges';
import LoginHeader from './components/LoginHeader';
import LoginFooter from './components/LoginFooter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SmartLoadingBuffer from '../../components/ui/SmartLoadingBuffer';
import useAuth from '../../hooks/useAuth';

const Login = () => {
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    loginWithProvider,
    authPending,
    error,
    clearError,
    demoLoginCredentials,
    pendingVerificationEmail,
    resendConfirmationEmail,
  } = useAuth();
  const [localError, setLocalError] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState({
    success: false,
    error: null,
  });

  useEffect(() => {
    clearError();
    setLocalError(null);
    setResendStatus({ success: false, error: null });
  }, [clearError]);

  const destination = useMemo(() => {
    const fromState = location?.state?.from;
    if (fromState?.pathname) {
      return fromState.pathname;
    }
    return "/dashboard";
  }, [location]);

  const handleLogin = async (formData) => {
    try {
      await login(formData);
      navigate(destination, { replace: true });
    } catch (err) {
      setLocalError(
        err?.message || "An unexpected error occurred. Please try again.",
      );
    }
  };

  const handleResendConfirmation = async () => {
    if (
      !pendingVerificationEmail &&
      !localError?.toLowerCase?.()?.includes("email")
    ) {
      setResendStatus({
        success: false,
        error: "Enter your email and try signing in first.",
      });
      return;
    }

    const email = pendingVerificationEmail;
    if (!email) {
      setResendStatus({
        success: false,
        error:
          "We could not detect the email to verify. Try registering or signing in again.",
      });
      return;
    }

    setResendLoading(true);
    setResendStatus({ success: false, error: null });

    try {
      await resendConfirmationEmail(email);
      setResendStatus({ success: true, error: null });
    } catch (err) {
      setResendStatus({
        success: false,
        error:
          err?.message ||
          "Failed to resend confirmation email. Please try again.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleOAuthLogin = async (provider) => {
    try {
      await loginWithProvider(provider);
      navigate(destination, { replace: true });
    } catch (err) {
      setLocalError(
        err?.message || `${provider} authentication failed. Please try again.`,
      );
    }
  };

  const activeError = localError || error?.message || error;
  const unconfirmedEmail = pendingVerificationEmail;

  const greetingInfo = useMemo(() => {
    const hour = new Date().getHours();

    if (hour < 12) {
      return {
        title: "Good morning",
        caption: "Lock in a focused morning by syncing your learning plan first.",
        icon: "Sunrise",
      };
    }

    if (hour < 18) {
      return {
        title: "Good afternoon",
        caption: "Pick up where you left off—your habits and knowledge are ready.",
        icon: "Sun",
      };
    }

    return {
      title: "Good evening",
      caption: "Reflect on today and prime tomorrow’s insights before you log off.",
      icon: "MoonStar",
    };
  }, []);

  const loginHighlights = useMemo(
    () => [
      {
        icon: "BrainCircuit",
        title: "Adaptive review",
        description: "See flashcards and knowledge surfaced right when it matters.",
      },
      {
        icon: "Target",
        title: "Habit alignment",
        description: "Habits, reflections, and knowledge stay coordinated each day.",
      },
      {
        icon: "Sparkles",
        title: "AI briefings",
        description: "Receive smart recaps the moment you sign in to stay ahead.",
      },
    ],
    [],
  );

  return (
    <AuthenticationGuard requireAuth={false}>
      <div className="min-h-screen bg-background"> 
        <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left column - hero */}
          <div className="relative hidden lg:flex flex-col justify-between bg-gradient-to-br from-primary/15 via-background to-background p-12">
            <div className="space-y-6">
              <LoginHeader />
              <div className="bg-card/60 backdrop-blur border border-border/60 rounded-xl p-6 space-y-4">
                <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="Sparkles" size={18} className="text-primary" />
                  <span>Why learners trust Cogito</span>
                </h2>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <Icon name="BrainCircuit" size={16} className="mt-0.5 text-primary" />
                    <span>Adaptive plans that evolve with your study patterns.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Target" size={16} className="mt-0.5 text-warning" />
                    <span>Daily nudges to keep habits, knowledge, and flashcards in sync.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Shield" size={16} className="mt-0.5 text-success" />
                    <span>Privacy-first architecture backed by Supabase security.</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {loginHighlights.map((highlight) => (
                  <div
                    key={highlight.title}
                    className="h-full rounded-xl border border-border/70 bg-card/60 p-4 space-y-2 text-left shadow-subtle"
                  >
                    <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                      <Icon name={highlight.icon} size={16} className="text-primary" />
                      <span>{highlight.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {highlight.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-xs text-muted-foreground/80">
              <p>&copy; {new Date().getFullYear()} Cogito. Built for focused, evidence-based learning.</p>
            </div>
          </div>

          {/* Right column - form */}
          <div className="flex flex-col justify-center px-4 py-10 sm:px-8 lg:px-16 bg-background">
            <div className="w-full max-w-md mx-auto space-y-8">
              <div className="lg:hidden">
                <LoginHeader />
              </div>

              {/* Global error banner */}
              {activeError && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4 animate-slide-up">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-error mb-1">Authentication Failed</h4>
                      <p className="text-sm text-error/80">{activeError}</p>
                    </div>
                    {unconfirmedEmail && (
                      <div className="space-y-3">
                        <p className="text-xs text-error/70">
                          Please confirm your email address to activate your account. Check your inbox for a verification link sent to <span className="font-medium text-error">{unconfirmedEmail}</span>.
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResendConfirmation}
                            disabled={resendLoading || authPending}
                          >
                            {resendLoading ? 'Resending…' : 'Resend confirmation email'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setResendStatus({ success: false, error: null })}
                          >
                            Dismiss
                          </Button>
                        </div>
                        {resendStatus.success && (
                          <p className="text-xs text-success">
                            A new confirmation email has been sent. It may take a couple of minutes to arrive.
                          </p>
                        )}
                        {resendStatus.error && (
                          <p className="text-xs text-error">
                            {resendStatus.error}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* Sign in card */}
              <div className="bg-card rounded-2xl border border-border/80 shadow-subtle p-6 sm:p-8 space-y-6">
                <div className="space-y-4">
                  <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <Icon name={greetingInfo.icon} size={16} />
                    <span>{greetingInfo.title}</span>
                  </div>
                  <div className="space-y-2">
                    <h1 className="text-2xl font-semibold text-foreground">Sign in to your workspace</h1>
                    <p className="text-sm text-muted-foreground">
                      {greetingInfo.caption}
                    </p>
                  </div>
                </div>

                <LoginForm onSubmit={handleLogin} loading={authPending} error={activeError} />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden>
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-3 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                <OAuthButtons
                  loading={authPending}
                  onGoogleLogin={() => handleOAuthLogin('google')}
                  onGitHubLogin={() => handleOAuthLogin('github')}
                />

                <div className="text-sm text-muted-foreground text-center">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="text-primary font-medium hover:underline"
                  >
                    Create one now
                  </button>
                </div>
              </div>

              <LoginFooter />

              <div className="text-center">
                <button
                  onClick={() => setShowSecurityInfo(!showSecurityInfo)}
                  className="inline-flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showSecurityInfo ? 'ChevronUp' : 'ChevronDown'} size={16} />
                  <span>Security & Privacy Information</span>
                </button>
              </div>

              {showSecurityInfo && (
                <div className="animate-slide-up">
                  <SecurityBadges />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:hidden">
                {loginHighlights.map((highlight) => (
                  <div
                    key={highlight.title}
                    className="h-full rounded-xl border border-border/70 bg-card/60 p-4 space-y-2 text-left shadow-subtle"
                  >
                    <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                      <Icon name={highlight.icon} size={16} className="text-primary" />
                      <span>{highlight.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {highlight.description}
                    </p>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>

        {authPending && (
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-50">
            <SmartLoadingBuffer
              showLogo={false}
              className="max-w-md"
              title="Signing you in"
              message="Securing your session and preparing your learning map."
              hint="If this takes longer than a few seconds, refresh the page."
            />
          </div>
        )}
      </div>
    </AuthenticationGuard>
  );
};

export default Login;
